import type { SetupCheckName } from "./onboarding";

import { z } from "zod";

export const AGENT_FREE_MONTH_MINIMUM_PAID_TENURE_MS = 7 * 24 * 60 * 60 * 1000;

export function isAgentFreeMonthPlanEligible({
  interval,
  plan,
  isManaged,
}: {
  interval: "month" | "year" | "monthly" | "yearly";
  plan: string | null;
  isManaged: boolean;
}): boolean {
  return (
    (interval === "month" || interval === "monthly") &&
    (plan === "starter" || plan === "pro") &&
    !isManaged
  );
}

export const agentBillingPlanSchema = z
  .enum(["starter", "pro"])
  .describe("AgentBillingPlan");

export type AgentBillingPlan = z.infer<typeof agentBillingPlanSchema>;

export const featureTypeSchema = z
  .enum([
    "credits",
    "shopifyThemeBuilder.publishedElements",
    "sites",
    "shopifyThemeBuilder.projects",
  ])
  .describe("FeatureType");

export type FeatureType = z.infer<typeof featureTypeSchema>;

export const creditBalanceTypeSchema = z
  .enum(["base", "purchase"])
  .describe("CreditBalanceType");

export type CreditBalanceType = z.infer<typeof creditBalanceTypeSchema>;

export const creditTransactionTypeSchema = z
  .enum([
    "base",
    "purchase",
    "track",
    "refund",
    "admin",
    "migration",
    "topup",
    // One-time activation reward grant (see rewardKeySchema / REWARD_CATALOG).
    "reward",
  ])
  .describe("CreditTransactionType");

export type CreditTransactionType = z.infer<typeof creditTransactionTypeSchema>;

// #region Activation rewards
//
// Named one-time credit rewards for completing activation tasks. This is the
// single shared contract so activation content, the trackers that grant them,
// and the reward UI can only ever reference known keys. Each reward settles to
// one ledger row keyed by its source key (see activationRewardSourceKey).
//
// Extends Kevin O'Connell's `rewardKey`/`REWARD_CATALOG` model from
// PR #24549 (a single flat onboarding reward) into per-task rewards.

export const rewardKeySchema = z
  .enum([
    // Brand + business context captured (BUSINESS.md / DESIGN.md written).
    "brand-business",
    // Replo connected to an AI assistant over MCP, verified from the account's
    // OAuth grants. Second because working from chat is what drives the rest.
    "connect-assistant",
    // Store connected for checkout — Shopify or Stripe payments ready.
    "connect-store",
    // First third-party integration connected (Klaviyo, GA, Meta, etc.).
    "first-integration",
    // A custom domain connected to the project.
    "custom-domain",
    // A performance report, reusable project skill, and scheduled task exist.
    "growth-workflow",
    "home-product",
    "home-page",
    "home-publish",
    "home-connection",
    "home-slack",
    "home-claude",
    "home-chatgpt",
    // One reward per Home card; `REWARD_CATALOG` names what each one pays for.
    "home-store",
    "home-funnel",
    "home-storefront",
    "home-research",
    "home-launch",
    "home-domain",
    "home-products",
    "home-idea",
    "home-build-shopify-product-page",
    "home-build-shopify-listicle",
    "home-build-shopify-advertorial",
    "home-build-shopify-bundle",
    "home-copy-shopify-page",
    "home-copy-competitor-page",
    "home-start-shopify-template",
    "home-build-shopify-quiz",
    "home-audit-shopify-page",
    "home-build-shopify-persona-page",
    "home-build-shopify-ab-variant",
    "home-suggest-shopify-pages",
    "home-import-shopify-figma",
    "home-build-shopify-ad-page",
    "home-compare-competitor-offers",
    "home-find-creative-angles",
    "home-build-swipe-file",
    "home-write-creative-brief",
  ])
  .describe("RewardKey");

export type RewardKey = z.infer<typeof rewardKeySchema>;

export const REWARD_KEYS = rewardKeySchema.options;

/** Card order, then the retired step rewards (0 credits) so their done state still reaches the client. */
export const HOME_REWARD_KEYS: readonly RewardKey[] = [
  "home-page",
  "home-store",
  "home-connection",
  "home-slack",
  "home-claude",
  "home-chatgpt",
  "home-funnel",
  "home-storefront",
  "home-research",
  "home-launch",
  "home-domain",
  "home-products",
  "home-idea",
  "home-build-shopify-product-page",
  "home-build-shopify-listicle",
  "home-build-shopify-advertorial",
  "home-build-shopify-bundle",
  "home-copy-shopify-page",
  "home-copy-competitor-page",
  "home-start-shopify-template",
  "home-build-shopify-quiz",
  "home-audit-shopify-page",
  "home-build-shopify-persona-page",
  "home-build-shopify-ab-variant",
  "home-suggest-shopify-pages",
  "home-import-shopify-figma",
  "home-build-shopify-ad-page",
  "home-compare-competitor-offers",
  "home-find-creative-angles",
  "home-build-swipe-file",
  "home-write-creative-brief",
  "home-product",
  "home-publish",
];

/** Account-wide cap; the final reward is clamped to the remaining balance. */
export const HOME_REWARD_CAP_CREDITS = 500;

export type RewardDefinition = {
  /** Short, user-facing title of the task that earns the reward. */
  title: string;
  /** Reward size in credits. 100 credits = $1 (1 credit = 1¢). */
  amountCredits: number;
  /**
   * What the reward is evidence of, and so how often it can be earned.
   *
   * `workspace` is the default because doing the work again in a new workspace
   * is real work — an agency connecting a second client's store has earned it
   * twice. `user` is for the ones whose evidence is not project-scoped at all:
   * granting those per workspace pays repeatedly for one act.
   */
  scope: "workspace" | "user";
  /** The check that decides the task is done. Several rewards share one check. */
  check: SetupCheckName;
};

// Client reads this only to display what a reward is worth; the amount is
// granted server-side. Amounts are tunable without changing the ledger model.
export const REWARD_CATALOG: Record<RewardKey, RewardDefinition> = {
  "home-compare-competitor-offers": {
    title: "Compare competitor offers",
    amountCredits: 50,
    scope: "workspace",
    check: "home-research",
  },
  "home-find-creative-angles": {
    title: "Find new creative angles",
    amountCredits: 50,
    scope: "workspace",
    check: "home-research",
  },
  "home-build-swipe-file": {
    title: "Build a creative swipe file",
    amountCredits: 50,
    scope: "workspace",
    check: "home-research",
  },
  "home-write-creative-brief": {
    title: "Write a creative test brief",
    amountCredits: 50,
    scope: "workspace",
    check: "home-research",
  },
  "home-build-shopify-product-page": {
    title: "Build a Shopify product page",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-build-shopify-listicle": {
    title: "Build a Shopify listicle",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-build-shopify-advertorial": {
    title: "Build a Shopify advertorial",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-build-shopify-bundle": {
    title: "Build a bundle builder",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-copy-shopify-page": {
    title: "Copy an existing landing page",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-copy-competitor-page": {
    title: "Copy a competitor's page",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-start-shopify-template": {
    title: "Start from a template",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-build-shopify-quiz": {
    title: "Build a product quiz",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-audit-shopify-page": {
    title: "Run a CRO page audit",
    amountCredits: 50,
    scope: "workspace",
    check: "home-research",
  },
  "home-build-shopify-persona-page": {
    title: "Build a page for a customer persona",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-build-shopify-ab-variant": {
    title: "Build an A/B test variant",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-suggest-shopify-pages": {
    title: "Get new page ideas",
    amountCredits: 50,
    scope: "workspace",
    check: "home-research",
  },
  "home-import-shopify-figma": {
    title: "Import a Figma design for Shopify",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-build-shopify-ad-page": {
    title: "Turn an ad into a landing page",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },

  // Retired step rewards; kept so ledger rows keyed by them still parse.
  "home-product": {
    title: "Choose or create a product",
    amountCredits: 0,
    scope: "workspace",
    check: "home-product",
  },
  "home-page": {
    title: "Build a landing page",
    amountCredits: 100,
    scope: "workspace",
    check: "home-page",
  },
  "home-publish": {
    title: "Launch a campaign page",
    amountCredits: 0,
    scope: "workspace",
    check: "has-published",
  },
  "home-connection": {
    title: "Use Replo from Slack, Claude, or ChatGPT",
    amountCredits: 0,
    scope: "user",
    check: "home-connection",
  },
  "home-slack": {
    title: "Use Replo from Slack",
    amountCredits: 25,
    scope: "user",
    check: "home-connection",
  },
  "home-claude": {
    title: "Use Replo from Claude",
    amountCredits: 25,
    scope: "user",
    check: "home-connection",
  },
  "home-chatgpt": {
    title: "Use Replo from ChatGPT",
    amountCredits: 25,
    scope: "user",
    check: "home-connection",
  },
  "home-store": {
    title: "Connect your store",
    amountCredits: 50,
    scope: "workspace",
    check: "has-connected-tool",
  },
  "home-funnel": {
    title: "Build a sales funnel",
    amountCredits: 100,
    scope: "workspace",
    check: "home-funnel",
  },
  "home-storefront": {
    title: "Set up a store with checkout",
    amountCredits: 100,
    scope: "workspace",
    check: "home-storefront",
  },
  "home-research": {
    title: "Research competitor ads",
    amountCredits: 50,
    scope: "workspace",
    check: "home-research",
  },
  "home-launch": {
    title: "Launch a campaign page",
    amountCredits: 50,
    scope: "workspace",
    check: "has-published",
  },
  "home-domain": {
    title: "Connect your domain",
    amountCredits: 50,
    scope: "workspace",
    check: "custom-domain",
  },
  "home-products": {
    title: "Import your products",
    amountCredits: 50,
    scope: "workspace",
    check: "home-products",
  },
  "home-idea": {
    title: "Validate a product idea",
    amountCredits: 50,
    scope: "workspace",
    check: "home-product",
  },
  "brand-business": {
    title: "Set up your brand",
    amountCredits: 250,
    scope: "workspace",
    check: "brand-business",
  },
  "connect-store": {
    title: "Connect your store",
    amountCredits: 400,
    scope: "workspace",
    check: "connect-store",
  },
  "first-integration": {
    title: "Connect an integration",
    amountCredits: 250,
    scope: "workspace",
    check: "first-integration",
  },
  "custom-domain": {
    title: "Add a custom domain",
    amountCredits: 300,
    scope: "workspace",
    check: "custom-domain",
  },
  "connect-assistant": {
    title: "Connect ChatGPT or Claude",
    amountCredits: 400,
    scope: "user",
    check: "connect-assistant",
  },
  "growth-workflow": {
    title: "Build your growth workflow",
    amountCredits: 400,
    scope: "workspace",
    check: "growth-workflow",
  },
};

export const ACTIVATION_REWARD_SOURCE_PREFIX = "activation-reward:";

/**
 * The ledger's idempotency key for one reward: readable note and dedupe key
 * in one. Workspace-scoped tasks carry no suffix -- the partial unique index
 * on (workspace_id, source_key) makes them once per workspace. User-scoped
 * tasks embed the user id, so the same person earning from another workspace
 * is caught by grantOnce's bare-key pre-check.
 */
export function activationRewardSourceKey({
  task,
  userId,
}: {
  task: RewardKey;
  userId: string;
}): string {
  return REWARD_CATALOG[task].scope === "user"
    ? `${ACTIVATION_REWARD_SOURCE_PREFIX}${task}:user:${userId}`
    : `${ACTIVATION_REWARD_SOURCE_PREFIX}${task}`;
}

/** The task a granted source key names, or null for non-reward rows. */
export function activationTaskFromSourceKey(
  sourceKey: string,
): RewardKey | null {
  if (!sourceKey.startsWith(ACTIVATION_REWARD_SOURCE_PREFIX)) {
    return null;
  }
  const task = sourceKey
    .slice(ACTIVATION_REWARD_SOURCE_PREFIX.length)
    .split(":")[0];
  const parsed = rewardKeySchema.safeParse(task);
  return parsed.success ? parsed.data : null;
}

export const grantOnceInputSchema = z
  .object({
    workspaceId: z.uuid(),
    /** Who earned it; recorded on the row. */
    userId: z.uuid(),
    /** Server-owned by the caller's catalog; never client-supplied. */
    amountCredits: z.number().int().positive(),
    /** Idempotency key and human-readable note in one. */
    sourceKey: z.string().min(1).max(255),
    /**
     * Default (false) dedupes per workspace, matching the partial unique
     * index -- the same key re-earns in another workspace. True widens the
     * pre-check to every workspace, for keys that pay a person once.
     */
    dedupeAcrossWorkspaces: z.boolean().optional(),
    /** A user-wide ceiling for keys under `prefix`; the ledger clamps inside its transaction so racing grants cannot overpay. */
    cap: z
      .object({
        prefix: z.string().min(1).max(255),
        maxCredits: z.number().int().positive(),
      })
      .optional(),
  })
  .describe("GrantOnceInput");

export type GrantOnceInput = z.infer<typeof grantOnceInputSchema>;

export const grantOnceResultSchema = z
  .object({
    /** True when this call created the grant; false when it already existed. */
    granted: z.boolean(),
    alreadyGranted: z.boolean(),
    /** Credits added by this call, or the existing grant's amount when it was already granted. */
    amountCredits: z.number().int().nonnegative(),
  })
  .describe("GrantOnceResult");

export type GrantOnceResult = z.infer<typeof grantOnceResultSchema>;

export const grantedSourceKeysInputSchema = z
  .object({
    workspaceId: z.uuid(),
    userId: z.uuid(),
    /** Only keys under this prefix are returned. */
    prefix: z.string().min(1).max(255),
  })
  .describe("GrantedSourceKeysInput");

export const grantedSourceKeysResultSchema = z
  .object({
    sourceKeys: z.array(z.string()),
  })
  .describe("GrantedSourceKeysResult");

export const grantedCreditsSumInputSchema = z
  .object({
    userId: z.uuid(),
    /** Source-key prefix to sum under, e.g. the Home reward prefix. */
    prefix: z.string().min(1),
  })
  .describe("GrantedCreditsSumInput");

export const grantedCreditsSumResultSchema = z
  .object({
    /** Credits this user was granted under the prefix, in every workspace. */
    credits: z.number().int().nonnegative(),
  })
  .describe("GrantedCreditsSumResult");

// #endregion

// #region Per-message tracking + workspace billing prefs (agent-ui)

export const trackInputSchema = z
  .object({
    workspaceId: z.uuid(),
    messageId: z.uuid(),
  })
  .describe("CreditTrackInput");

export type CreditTrackInput = z.infer<typeof trackInputSchema>;

export const trackResultSchema = z
  .object({
    alreadyBilled: z.boolean(),
    creditsDeducted: z.number().int().nonnegative(),
    newBalanceCredits: z.number().int(),
    topupTriggered: z.boolean(),
    topupFailed: z.boolean(),
  })
  .describe("CreditTrackResult");

export type CreditTrackResult = z.infer<typeof trackResultSchema>;

// Vendor-cost charges (Apify, DataForSEO): bedrock reports raw USD, publisher
// applies the markup and conversion. `sourceKey` is the idempotency key.
export const trackSourceInputSchema = z
  .object({
    workspaceId: z.uuid(),
    sourceKey: z.string().min(1).max(255),
    costUsd: z.number().nonnegative(),
  })
  .describe("CreditTrackSourceInput");

export type CreditTrackSourceInput = z.infer<typeof trackSourceInputSchema>;

export const creditCheckResultSchema = z
  .object({
    isAllowed: z.boolean(),
    balance: z.number().int().nullable(),
  })
  .describe("CreditCheckResult");

export type CreditCheckResult = z.infer<typeof creditCheckResultSchema>;

export const workspaceBillingPreferencesSchema = z
  .object({
    topupEnabledAt: z.coerce.date().nullable(),
    topupThreshold: z.number().int().positive().nullable(),
    topupRefillTarget: z.number().int().positive().nullable(),
    topupFailedAt: z.date().nullable(),
    monthlySpendLimit: z.number().int().positive().nullable(),
  })
  .describe("WorkspaceBillingPreferences");

export type WorkspaceBillingPreferences = z.infer<
  typeof workspaceBillingPreferencesSchema
>;

export const dailySpendPointSchema = z
  .object({
    day: z.string(), // YYYY-MM-DD UTC
    cents: z.number().int().nonnegative(),
  })
  .describe("DailySpendPoint");

export type DailySpendPoint = z.infer<typeof dailySpendPointSchema>;

// #endregion

export const creditBalanceIntervalSchema = z
  .enum(["month", "one_off"])
  .describe("CreditBalanceInterval");

export type CreditBalanceInterval = z.infer<typeof creditBalanceIntervalSchema>;

export const adminCreditBalanceIntervalByType = {
  base: "month",
  purchase: "one_off",
} satisfies Record<CreditBalanceType, CreditBalanceInterval>;

export const agentBillingSubscriptionIntervalSchema = z
  .enum(["month", "year"])
  .describe("AgentBillingSubscriptionInterval");

export type AgentBillingSubscriptionInterval = z.infer<
  typeof agentBillingSubscriptionIntervalSchema
>;

export const priceIntervalSchema = z
  .enum(["month", "year", "one_off"])
  .describe("PriceInterval");

export type PriceInterval = z.infer<typeof priceIntervalSchema>;

// #region Rate card types

export type Price = {
  interval: PriceInterval;
  /** Price in minimum currency units (e.g. 12900 = $129.00 USD) */
  amount: number;
  currency: string;
  priceId: string;
  productId?: string;
  isDefault?: boolean;
  isSuperuser?: boolean;
  isDeprecated?: boolean;
};

export type Feature = {
  featureType: FeatureType;
  units: number | null;
  minimumCapacity: number;
};

export type Product = {
  productType: string;
  featureType: FeatureType;
  displayName: string;
  units: number;
  isEligible: boolean;
  maxProductCount: number | null;
  currentProductCount: number;
  prices: Price[];
};

export type RateCard = {
  rank: number | null;
  plan: AgentBillingPlan | null;
  canUpgrade: boolean;
  title: string;
  prices: Price[];
  features: Feature[];
  addOnProducts: Product[];
};

// #endregion

// #region Subscription view

export const PLAN_PRODUCT_TYPES = new Set<string>(
  agentBillingPlanSchema.options,
);

export const subscriptionItemSchema = z
  .object({
    productType: z.string(),
    priceId: z.string(),
    quantity: z.number().int().positive(),
    interval: agentBillingSubscriptionIntervalSchema,
  })
  .describe("SubscriptionItem");

export type SubscriptionItem = z.infer<typeof subscriptionItemSchema>;

export const subscriptionViewSchema = z
  .object({
    subscriptionItems: z.array(subscriptionItemSchema),
  })
  .describe("SubscriptionView");

export type SubscriptionView = z.infer<typeof subscriptionViewSchema>;

// #endregion

export const stripeSubscriptionStatusSchema = z
  .enum([
    "active",
    "past_due",
    "canceled",
    "incomplete",
    "incomplete_expired",
    "trialing",
    "paused",
    "unpaid",
  ])
  .describe("StripeSubscriptionStatus");

export type StripeSubscriptionStatus = z.infer<
  typeof stripeSubscriptionStatusSchema
>;

// #region Subscription types

export type AgentSubscription = {
  id: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  /**
   * Mirror of the Stripe sub status. Narrowed to the "currently-live" set that
   * `SubscriptionService.findActive` returns (active | trialing | past_due) so
   * UI can branch on trial state without an extra round-trip to Stripe.
   */
  status: StripeSubscriptionStatus;
  startedAt: Date;
  cancelAt: Date | null;
  currentPeriodEnd: Date | null;
  interval: AgentBillingSubscriptionInterval;
  plan: AgentBillingPlan;
  /** Total subscription amount in minimum currency units per billing interval */
  amount: number;
  currency: string;
  /** True when any item sits on an off-catalog contract price; UI shows "Custom" and locks self-serve changes. (yuxin, 2026-07-14, REPL-29329) */
  hasCustomPricing: boolean;
  items: {
    id: string;
    stripePriceId: string;
    stripeProductId: string;
    /** Price in minimum currency units (e.g. 12900 = $129.00 USD) */
    unitAmount: number;
    quantity: number;
    productType: string;
    currency: string;
  }[];
};

// #endregion
